import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Monitor } from "lucide-react";
import { WEB_APP_URL, WEB_APP_ENABLED } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { SmartLink } from "@/components/SmartLink";
import { useLocation } from "react-router-dom";
import { LanguageToggle } from "@/components/LanguageToggle";

const useActiveSection = (ids) => {
  const [active, setActive] = useState(null);
  useEffect(() => {
    let raf = 0;
    const compute = () => {
      raf = 0;
      const marker = window.scrollY + window.innerHeight * 0.4;
      let current = null;
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= marker) current = id;
      });
      if (window.scrollY < 80) current = null;
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [ids]);
  return active;
};

export const Header = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const idsKey = t.nav.items.map((i) => i.id).join(",");
  const ids = useMemo(() => idsKey.split(","), [idsKey]);
  const { pathname } = useLocation();
  const onLanding = pathname === "/";
  const sectionActive = useActiveSection(ids);
  const active = onLanding ? sectionActive : null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background,border-color] duration-300 ${
        scrolled || open ? "glass" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-6 md:px-8">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Hauptnavigation">
          {t.nav.items.map((item) => {
            const isActive = active === item.id;
            return (
              <SmartLink
                key={item.id}
                href={`/#${item.id}`}
                data-testid={`nav-${item.id}`}
                className={`relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-white" : "text-[var(--ink-2)] hover:text-white"
                }`}
              >
                {item.label}
                {isActive && (
                  <motion.span
                    layoutId="nav-active"
                    className="absolute inset-x-3 -bottom-0.5 h-px bg-[var(--sage)]"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
              </SmartLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle className="hidden sm:inline-flex" />
          {WEB_APP_ENABLED && (
            <a
              href={WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-web-app-btn"
              className="btn btn-ghost hidden px-4 py-2 text-sm xl:inline-flex"
            >
              <Monitor className="h-4 w-4 text-[var(--sage)]" />
              {t.nav.webApp}
            </a>
          )}
          <SmartLink
            href="/#preise"
            data-testid="header-cta-btn"
            className="btn btn-primary hidden px-4 py-2 text-sm md:inline-flex"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4" />
          </SmartLink>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--line-strong)] text-white lg:hidden"
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="glass lg:hidden"
          >
            <nav className="mx-auto flex max-w-7xl flex-col px-5 pb-6 pt-2 sm:px-6" aria-label="Mobile Navigation">
              {t.nav.items.map((item, i) => (
                <SmartLink
                  key={item.id}
                  href={`/#${item.id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="flex items-center justify-between border-b border-[var(--line)] py-4 font-display text-xl text-[var(--ink)]"
                >
                  {item.label}
                  <span className="font-mono text-xs text-[var(--ink-3)]">0{i + 1}</span>
                </SmartLink>
              ))}
              {WEB_APP_ENABLED && (
                <a
                  href={WEB_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-web-app"
                  className="flex items-center justify-between border-b border-[var(--line)] py-4 font-display text-xl text-[var(--sage)]"
                >
                  {t.nav.webApp}
                  <Monitor className="h-5 w-5" />
                </a>
              )}
              <div className="mt-5 flex items-center justify-between gap-4">
                <LanguageToggle />
                <SmartLink
                  href="/#preise"
                  onClick={() => setOpen(false)}
                  className="btn btn-primary flex-1 px-5 py-3 text-sm"
                >
                  {t.nav.cta}
                </SmartLink>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
